import api, { isAuthError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Adaptive save/share:
 *  - Mobile (Web Share API with files): opens the native share sheet
 *    (WhatsApp, Gmail, etc.) on Android & iOS.
 *  - Desktop (File System Access API): prompts the user to choose the
 *    folder/location to save the PDF.
 *  - Otherwise: falls back to the iframe-safe new-tab download.
 */
export async function smartSaveOrShare(path, filename) {
  const name = filename || "documento.pdf";
  const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent) ||
    (navigator.maxTouchPoints && navigator.maxTouchPoints > 1);

  // Fetch the PDF bytes (with a hard timeout so the UI can never hang forever).
  let blob;
  try {
    const { data } = await api.get(path, { responseType: "blob", timeout: 30000 });
    blob = data;
  } catch (e) {
    // Network/timeout/auth issue → fall back to the server-side share copy + new tab.
    return authenticatedDownload(path, name);
  }

  // 1) Mobile: native share sheet with the actual file (WhatsApp / Gmail / ...).
  if (isMobile) {
    try {
      const file = new File([blob], name, { type: "application/pdf" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: name });
        return;
      }
    } catch (e) {
      if (e && e.name === "AbortError") return; // user cancelled the share sheet
      // Share failed (e.g. lost user activation) → reliable fallback, do NOT
      // fall through into the desktop file-picker branch.
      return authenticatedDownload(path, name);
    }
    // No share support on this mobile browser → reliable fallback.
    return authenticatedDownload(path, name);
  }

  // 2) Desktop: let the user pick the destination folder/location.
  if (typeof window.showSaveFilePicker === "function") {
    try {
      const handle = await window.showSaveFilePicker({
        suggestedName: name,
        types: [{ description: "PDF", accept: { "application/pdf": [".pdf"] } }],
      });
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      toast.success("Documento guardado");
      return;
    } catch (e) {
      if (e && e.name === "AbortError") return; // user cancelled the picker
      // Picker unavailable / activation lost → fall through to the generic save.
    }
  }

  // 3) Generic desktop fallback. Inside the sandboxed preview iframe a blob
  //    download is blocked, so use the server share-copy + new-tab method.
  const inIframe = (() => { try { return window.self !== window.top; } catch { return true; } })();
  if (inIframe) {
    return authenticatedDownload(path, name);
  }
  try {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
    toast.success("Descarga iniciada");
  } catch (e) {
    await authenticatedDownload(path, name);
  }
}

/**
 * Authenticated download — works inside the Emergent iframe sandbox.
 *
 * Problem: the SPA runs inside an iframe with a sandbox attribute that omits
 * `allow-downloads`. Any blob download triggered from within the iframe is
 * silently blocked: the toast fires but no file lands on disk.
 *
 * Solution: ask the backend for a SHARABLE COPY of the document — the server
 * writes the PDF to `/app/frontend/public/downloads/docs/<token>.pdf` and
 * returns the public URL. We then open it via a real anchor element with
 * `target="_blank"`, which the browser treats as a user-initiated top-level
 * navigation and is NEVER blocked by the iframe sandbox.
 *
 * The `path` param controls which sharable endpoint we hit:
 *   - "/documents/certificate"           → "/documents/certificate/share"
 *   - "/documents/receipt/<tx_id>"       → "/documents/receipt/<tx_id>/share"
 */
export async function authenticatedDownload(path, filename) {
  // Map the legacy direct-stream path to its /share counterpart
  const sharePath = path.endsWith("/share") ? path : path + "/share";
  let info;
  try {
    const { data } = await api.get(sharePath);
    info = data;
  } catch (e) {
    if (isAuthError(e)) toast.error("Tu sesión expiró, vuelve a iniciar sesión");
    else toast.error("No se pudo generar el documento");
    return;
  }

  const fullUrl = `${process.env.REACT_APP_BACKEND_URL || ""}${info.url}`;

  // Use a real anchor click: user-initiated, target="_blank" — bypasses the iframe sandbox.
  const a = document.createElement("a");
  a.href = fullUrl;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  a.download = filename || info.filename;
  document.body.appendChild(a);
  a.click();
  a.remove();

  toast.success("Descarga iniciada en una pestaña nueva", { duration: 4000 });
}

/**
 * Authenticated view — open the PDF inline in a new browser tab so the user
 * can read it AND use the native PDF viewer "Save" button.
 */
export async function authenticatedView(path) {
  const sharePath = path.endsWith("/share") ? path : path + "/share";
  try {
    const { data } = await api.get(sharePath);
    // Prefer the inline preview_url for "Ver"; fall back to url if older payload
    const url = data.preview_url || data.url;
    const fullUrl = `${process.env.REACT_APP_BACKEND_URL || ""}${url}`;
    const a = document.createElement("a");
    a.href = fullUrl;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } catch (e) {
    if (isAuthError(e)) toast.error("Tu sesión expiró, vuelve a iniciar sesión");
    else toast.error("No se pudo abrir el documento");
  }
}

/**
 * Just resolve the public URL of a sharable doc, useful when the UI wants to
 * render a visible <a href="..."> link the user can right-click and "Save as".
 */
export async function resolveShareUrl(path) {
  const sharePath = path.endsWith("/share") ? path : path + "/share";
  const { data } = await api.get(sharePath);
  return `${process.env.REACT_APP_BACKEND_URL || ""}${data.url}`;
}
